import { useEffect, useRef, useState } from 'react';
import { ModelPicker } from './ModelPicker';
import { ModelOptions } from './ModelOptions';
import { useBench } from './quick/useBench';
import { QuoteLine } from './quick/parts';
import { sayQuote } from '../lib/quick';
import { sayWhat } from '../lib/models';
import { api, uploadToSignedUrl, kindFor, type Asset, type Brand, type Idea, type ModelInfo } from '../lib/api';
import { TemplateGallery } from './TemplateGallery';
import {
  Details, KindTabs, Thumb, basename, displayName, sizeOf, sortAssets,
  type Kind, type SortKey,
} from './AssetGrid';

const TONE: Record<Asset['kind'], [string, string]> = {
  video: ['var(--red-wash)', 'var(--red)'],
  audio: ['var(--green-wash)', 'var(--green)'],
  image: ['var(--blue-wash)', 'var(--blue)'],
  document: ['var(--surface-3)', 'var(--ink-2)'],
};

export function Assets({ brand, prefill, onUsedPrefill }: {
  brand: Brand | null;
  /** A `make_picture` proposal, or the Quick Studio's Image tab as it stood: read once into the prompt, then handed back. */
  prefill?: Idea | null;
  onUsedPrefill?: () => void;
}) {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [genPrompt, setGenPrompt] = useState('');
  const [genBusy, setGenBusy] = useState(false);
  // 204: the model the picture is made on, its size, and the server's quote for one.
  const [imageModels, setImageModels] = useState<ModelInfo[] | null>(null);
  useEffect(() => { let live = true; api.models('image').then((m) => { if (live) setImageModels(m); }).catch(() => undefined); return () => { live = false; }; }, []);
  const bench = useBench('image', imageModels ?? undefined);
  const [note, setNote] = useState<string | null>(null);
  const [pct, setPct] = useState<number | null>(null);
  const [viewing, setViewing] = useState<{ asset: Asset; url: string } | null>(null);
  const [kind, setKind] = useState<Kind | 'all'>('all');
  const [sort, setSort] = useState<SortKey>('newest');
  const [asCards, setAsCards] = useState(true);
  const [editing, setEditing] = useState<Asset | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // 156: the creator's shelves — a group chip filters; 'none' is what sits on no shelf.
  const [group, setGroup] = useState<string | null>(null);
  const [groups, setGroups] = useState<{ name: string; count: number }[]>([]);
  // 192: the template store, beside the library — free to take into it.
  const [store, setStore] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const byGroup = group === null ? assets : group === 'none' ? assets.filter((a) => !a.group_name) : assets.filter((a) => a.group_name === group);
  const shown = sortAssets(kind === 'all' ? byGroup : byGroup.filter((a) => a.kind === kind), sort);

  /**
   * Put this file where a turn can cut to it.
   *
   * The rundown's footage picker lists SOURCES, not assets — so a file in the
   * library was invisible to it, and the only way to use one was to upload the
   * same file a second time. This is the missing step between the two, and it
   * copies nothing.
   */
  async function useAsFootage(a: Asset) {
    setBusy(a.id); setError(null); setNote(null);
    try {
      const src = await api.sourceFromAsset(a.id, displayName(a), a.kind === 'image' ? 'file' : 'tape');
      setNote(`"${src.name}" is now available as footage — pick it on a line in the card's script.`);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  }

  async function remove(a: Asset) {
    // Names the thing, because a library of similar files is exactly where a
    // confirmation that says "this asset" gets clicked through.
    if (!window.confirm(`Delete ${displayName(a)}? The file is removed too, and that cannot be undone.`)) return;
    setBusy(a.id); setError(null);
    try { await api.deleteAsset(a.id); await load(); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  }

  async function saveDetails(a: Asset, name: string, notes: string, groupName = '') {
    setBusy(a.id); setError(null);
    try {
      await api.updateAsset(a.id, { name: name.trim() || null, notes: notes.trim() || null, group: groupName.trim() || null });
      setEditing(null);
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  }

  async function load() {
    try { setAssets(await api.assets(brand?.id ?? null)); setError(null); }
    catch (e) { setError((e as Error).message); }
    api.assetGroups().then((g) => setGroups(g.groups)).catch(() => undefined);
  }
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [brand?.id]);

  // The proposal fills the prompt and then is let go: a starting point the
  // creator edits, never a command. Same contract as the Scene Builder's.
  useEffect(() => {
    if (!prefill || prefill.action !== 'make_picture') return;
    const p = String(prefill.args.prompt ?? '');
    if (p) setGenPrompt(p);
    onUsedPrefill?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  async function onFile(file: File) {
    setError(null);
    setPct(0);
    try {
      // Reserve the row first: the server derives the storage key from the
      // tenant in our token, so the client never names its own path.
      const { asset, upload } = await api.createAsset({
        brandId: brand?.id ?? null,
        filename: file.name,
        kind: kindFor(file),
        contentType: file.type || 'application/octet-stream',
      });
      await uploadToSignedUrl(upload.url, file, setPct);

      // The upload lands in a bucket that playback never reads from — that
      // separation is deliberate, so unscanned bytes cannot be served as
      // finished media. Promotion is what carries it across, and it is the
      // step that was missing: every asset uploaded here stayed 'pending' and
      // was permanently undownloadable, which is what "not ready yet" meant.
      await api.promoteAsset(asset.id);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setPct(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  /** Carry an asset across from the upload bucket. */
  async function promote(a: Asset) {
    setError(null);
    try { await api.promoteAsset(a.id); await load(); }
    catch (e) { setError((e as Error).message); }
  }

  /**
   * Every ready picture nobody has looked at, one after another.
   *
   * Sequential on purpose: it is a model call per picture, and forty at once
   * is forty concurrent requests from one browser tab against a rate limit.
   * The count in the button says how many are left, which is also the honest
   * cost.
   */
  // Goes through every picture, and says at the end what happened to the ones
  // it could not do. It used to stop at the first refusal — a picture over the
  // inline cap — with thirty still waiting behind it. The API now makes a
  // small copy of such a picture and asks for a second pass, so the count of
  // those is the useful thing to report.
  async function describeAll() {
    const todo = assets.filter((a) => (a.kind === 'video' || a.kind === 'image') && a.status === 'ready' && !a.described_at);
    setError(null); setNote(null);
    let done = 0; let later = 0; let failed: string | null = null;
    for (const a of todo) {
      try { await api.describeAsset(a.id); done += 1; }
      catch (e) {
        const msg = (e as Error).message;
        if (/small copy/.test(msg)) later += 1; else failed = msg;
      }
    }
    await load();
    const parts = [`${done} labelled`];
    if (later) parts.push(`${later} being copied down to size — press Describe again in a minute`);
    setNote(parts.join(' · '));
    if (failed) setError(failed);
  }

  // The same shape as promote: one asset busy at a time, the error shown once.
  async function describe(a: Asset) {
    setError(null);
    try { await api.describeAsset(a.id); await load(); }
    catch (e) { setError((e as Error).message); }
  }

  /**
   * Opened IN the page, not navigated to.
   *
   * A signed URL is a bearer credential with a ten-minute life. As the src of a
   * <video>, <audio> or <img> it stays inside the document; opened in a tab it
   * lands in the address bar and in browser history, where it outlives the tab
   * and is readable by anyone who has the machine until it expires. Same vector
   * as R31.
   *
   * A document has nothing to render it with, so it is fetched through the API
   * and handed over as a file — which is what the person wanted anyway.
   */
  async function play(a: Asset) {
    try {
      if (a.kind === 'document') {
        // The name it is SAVED as. Same reasoning as displayName: the media
        // key's basename is the asset's uuid, so downloads were landing in
        // people's folders called 4f3c… instead of what they uploaded.
        await api.downloadAsset(a.id, displayName(a));
        return;
      }
      const { url } = await api.assetUrl(a.id);
      setViewing({ asset: a, url });
    } catch (e) { setError((e as Error).message); }
  }

  return (
    <>
      <div className="vhead" data-move="asset-failed">
        <div>
          <h2>Assets</h2>
          <p>
            Scoped to {brand ? <b>{brand.name}</b> : 'this workspace'}. Nothing is public; playback
            goes through a short-lived signed URL.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            ref={fileRef}
            type="file"
            id="asset-file"
            style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); }}
          />
          {/* Ask for a picture. It lands in the library as an upload would,
              promoting, and is usable a few minutes later. */}
          <form className="row" style={{ gap: 6 }} data-move="assets-make" onSubmit={(e) => {
            e.preventDefault();
            const prompt = genPrompt.trim();
            if (prompt.length < 5) return;
            setError(null); setGenBusy(true);
            api.generateAsset(prompt, brand?.id ?? null, { ...(bench.model ? { model: bench.model.id } : {}), ...(bench.sel.resolution ? { resolution: bench.sel.resolution } : {}), ...(bench.sel.quality ? { quality: bench.sel.quality } : {}) })
              .then(async () => { setGenPrompt(''); await load(); })
              .catch((err) => setError((err as Error).message))
              .finally(() => setGenBusy(false));
          }}>
            <input value={genPrompt} onChange={(e) => setGenPrompt(e.target.value)} disabled={genBusy}
                   placeholder="Make a picture: a sunlit kitchen with a wooden island, wide shot"
                   style={{ minWidth: 300 }} maxLength={500} />
            <button className="btn" type="submit" disabled={genBusy || genPrompt.trim().length < 5 || (bench.quote !== null && !bench.quote.balance.ok)} title={bench.credits === null ? 'Reading the price list…' : `${bench.credits.toLocaleString()} credits`}>
              {genBusy ? 'Making…' : bench.credits === null ? 'Generate' : `Generate · ${bench.credits.toLocaleString()} cr`}
            </button>
          </form>
          {/* 196: the bed bench moved to the Audio Lab, beside the voice bench. */}
          {(() => {
            const todo = assets.filter((a) => (a.kind === 'video' || a.kind === 'image') && a.status === 'ready' && !a.described_at).length;
            return todo > 0 ? (
              // Label every picture nobody has looked at. The count is the
              // honest cost: it is a model call per picture.
              <button className="btn" data-move="assets-describe" disabled={pct !== null || busy !== null}
                      title="Label what each picture shows, and give camera-named ones a real name"
                      onClick={() => void describeAll()}>
                Describe {todo} {todo === 1 ? 'picture' : 'pictures'}
              </button>
            ) : null;
          })()}
          <button className="btn pri" disabled={pct !== null} onClick={() => fileRef.current?.click()}>
            {pct === null ? 'Upload' : `Uploading ${pct}%`}
          </button>
        </div>
      </div>
      {/* 204: the model the next picture is made on, its size, and the price before the press. */}
      <div className="row wrap" style={{ gap: 8, alignItems: 'center', marginBottom: 10 }} data-tool="assets-model">
        <ModelPicker bench="image" models={imageModels} value={bench.model} onChange={bench.setModel} />
        <ModelOptions bench="image" model={bench.model} value={bench.sel} onChange={bench.setSel} />
        <QuoteLine text={bench.credits === null ? null : sayQuote(bench.credits, sayWhat('image', bench.model, bench.sel), bench.quote?.balance)} />
      </div>

      {error && <div className="err">{error}</div>}
      {note && (
        <div className="err" style={{ borderColor: 'var(--green)', color: 'var(--green)' }}>{note}</div>
      )}
      {pct !== null && <div className="bar"><i style={{ width: `${pct}%` }} /></div>}

      <div className="row wrap" style={{ gap: 12, alignItems: 'center', marginBottom: 14 }}>
        <KindTabs assets={byGroup} value={kind} onChange={setKind} />
        {groups.length > 0 && (
          <div className="seg" data-tool="asset-groups" style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <button type="button" className={`btn tiny${group === null ? ' pri' : ''}`} onClick={() => setGroup(null)}>All shelves</button>
            {groups.map((g) => <button key={g.name} type="button" className={`btn tiny${group === g.name ? ' pri' : ''}`} onClick={() => setGroup(g.name)} title={`${g.count} in this group`}>{g.name} <span className="sub-dim">{g.count}</span></button>)}
            <button type="button" className={`btn tiny${group === 'none' ? ' pri' : ''}`} onClick={() => setGroup('none')}>No group</button>
          </div>
        )}
        <button type="button" className={`btn tiny${store ? ' pri' : ''}`} onClick={() => setStore((v) => !v)} title="192: plates, rigs, frames, audio, backgrounds — free to take, made to be changed">Templates</button>
        <div style={{ flex: 1 }} />
        <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)} title="Order">
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="name">Name</option>
          <option value="largest">Largest first</option>
          <option value="kind">Kind</option>
        </select>
        <button className="btn" onClick={() => setAsCards(!asCards)}>
          {asCards ? 'List' : 'Cards'}
        </button>
      </div>
      {store && (
        <div style={{ marginBottom: 14 }}>
          <TemplateGallery brandId={brand?.id ?? null} onTaken={() => { void api.assets(brand?.id ?? null).then(setAssets).catch(() => undefined); void api.assetGroups().then((g) => setGroups(g.groups)).catch(() => undefined); }} />
        </div>
      )}

      {shown.length === 0 ? (
        <div className="empty">
          {assets.length === 0
            ? 'Nothing here yet. Upload a file to get started.'
            : `No ${kind === 'all' ? '' : kind + ' '}assets match.`}
        </div>
      ) : asCards ? (
        <div className="grid">
          {shown.map((a) => (
            <div key={a.id} className={`asset${a.status !== 'ready' ? ' unready' : ''}`}
                 style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 10 }}>
              <button
                style={{ all: 'unset', cursor: 'pointer' }}
                onClick={() => void (a.status === 'ready' ? play(a) : promote(a))}
                title={a.status === 'ready' ? 'Open' : 'Not carried across yet — click to finish it'}
              >
                <Thumb asset={a} size={200} />
              </button>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {displayName(a)}
                </div>
                <div className="sub-dim">{a.kind} · {sizeOf(a.bytes)}</div>
                {a.notes && <div className="sub-dim" style={{ marginTop: 4 }}>{a.notes}</div>}
                {a.error && <div className="sub-dim" style={{ color: 'var(--red)' }}>{a.error}</div>}
                {a.description && <div className="sub-dim asset-desc">{a.description}</div>}
                {a.tags && a.tags.length > 0 && (
                  <div className="labels" title={a.description ?? ''}>
                    {a.tags.slice(0, 8).map((t) => <span key={t} className="label">{t}</span>)}
                  </div>
                )}
              </div>
              <div className="row wrap" style={{ gap: 6 }}>
                <button className="btn" disabled={busy === a.id} onClick={() => setEditing(a)}>Details</button>
                {(a.kind === 'video' || a.kind === 'image') && a.status === 'ready' && (
                  <button className="btn" disabled={busy === a.id}
                          title="Make this available to cut to on a line of script"
                          onClick={() => void useAsFootage(a)}>Use as footage</button>
                )}
                {(a.kind === 'video' || a.kind === 'image') && a.status === 'ready' && (
                  // What the picture shows, so the director can match a line to
                  // it. Explicit rather than automatic on upload: it is a model
                  // call per picture, and a library of four hundred stills is
                  // four hundred of them.
                  <button className="btn" disabled={busy === a.id}
                          title={a.described_at ? 'Look again and relabel' : 'Label what this shows, so the director can cut to it by meaning'}
                          onClick={() => void describe(a)}>
                    {a.described_at ? 'Relabel' : 'Describe'}
                  </button>
                )}
                <button className="btn" disabled={busy === a.id} onClick={() => void remove(a)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <table>
          <thead><tr><th /><th>Name</th><th>Kind</th><th>Size</th><th>Added</th><th /></tr></thead>
          <tbody>
            {shown.map((a) => (
              <tr key={a.id}>
                <td style={{ width: 96 }}><Thumb asset={a} size={84} /></td>
                <td>
                  <button style={{ all: 'unset', cursor: 'pointer', fontWeight: 500 }}
                          onClick={() => void (a.status === 'ready' ? play(a) : promote(a))}>
                    {displayName(a)}
                  </button>
                  {a.notes && <div className="sub-dim">{a.notes}</div>}
                  {a.status !== 'ready' && <div className="sub-dim">{a.error ?? 'not carried across yet'}</div>}
                </td>
                <td style={{ color: 'var(--ink-2)' }}>{a.kind}</td>
                <td style={{ color: 'var(--ink-2)' }}>{sizeOf(a.bytes)}</td>
                <td style={{ color: 'var(--ink-2)' }}>{new Date(a.created_at).toLocaleDateString()}</td>
                <td className="r">
                  <button className="btn" style={{ marginRight: 6 }} disabled={busy === a.id}
                          onClick={() => setEditing(a)}>Details</button>
                  {(a.kind === 'video' || a.kind === 'image') && a.status === 'ready' && (
                    <button className="btn" style={{ marginRight: 6 }} disabled={busy === a.id}
                            onClick={() => void useAsFootage(a)}>Use as footage</button>
                  )}
                  <button className="btn" disabled={busy === a.id} onClick={() => void remove(a)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <Details
          asset={editing}
          busy={busy === editing.id}
          onCancel={() => setEditing(null)}
          onSave={(n: string, notes: string, g: string) => void saveDetails(editing, n, notes, g)}
          groups={groups.map((g) => g.name)}
        />
      )}


      {viewing && (
        <div
          className="lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={basename(viewing.asset.storage_key)}
          onClick={() => setViewing(null)}
          onKeyDown={(e) => { if (e.key === 'Escape') setViewing(null); }}
        >
          <div className="lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <header>
              <b>{basename(viewing.asset.storage_key)}</b>
              <button className="btn ghost" onClick={() => setViewing(null)} autoFocus>Close</button>
            </header>
            {viewing.asset.kind === 'video' ? (
              <video src={viewing.url} controls autoPlay style={{ width: '100%' }} />
            ) : viewing.asset.kind === 'audio' ? (
              <audio src={viewing.url} controls autoPlay style={{ width: '100%' }} />
            ) : (
              <img src={viewing.url} alt={basename(viewing.asset.storage_key)} style={{ maxWidth: '100%' }} />
            )}
            <p className="hint">
              This link is short-lived on purpose — reopen the asset if it stops playing.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
